import { GraphqlQueryResponse } from "@/Common/api/types/GraphqlQueryResponse";
import { Nullable } from "@/Common/interfaces/types";
import { IPayPeriodPayRate, IPayPeriodPto, IPayPeriodTimesCard, IPayPeriodWordedHours } from "@/DataPages/types/timeAndPayRates/models";
import { apiInstance, gql } from "@/services/httpAdapter";
import { errorLogger } from "@Common/helpers/logger";

export async function getPayRate(locationId: Nullable<string>, commissionCycleId: Nullable<string>, payPeriodId: Nullable<string>) {
	const query = gql`
		query getPayRate($locationId: String!, $commissionCycleId: String!, $payPeriodId: String!) {
			getPayRate(locationId: $locationId, commissionCycleId: $commissionCycleId, payPeriodId: $payPeriodId) {
				_id
				commissionCycleId
				payPeriodId
				payRates {
					user {
						_id
						fullName
						position
					}
					rate
					secondaries
				}
				createdOn
				lastModifiedOn
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
	};

	try {
		const response: GraphqlQueryResponse<"getPayRate", IPayPeriodPayRate> = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getPayRate;
	} catch (err) {
		errorLogger(err as Error, { function: "getPayRate", message: "Failed to get pay rate", variables });
		return null;
	}
}

export async function getWorkedHours(locationId: Nullable<string>, commissionCycleId: Nullable<string>, payPeriodId: Nullable<string>) {
	const query = gql`
		query getWorkedHours($locationId: String!, $commissionCycleId: String!, $payPeriodId: String!) {
			getWorkedHours(locationId: $locationId, commissionCycleId: $commissionCycleId, payPeriodId: $payPeriodId) {
				_id
				locationId
				commissionCycleId
				payPeriodId
				usersWorkedHours {
					user {
						_id
						fullName
						position
					}
					hours
				}
				createdAt
				updatedAt
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
	};

	try {
		const response: GraphqlQueryResponse<"getWorkedHours", IPayPeriodWordedHours> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getWorkedHours;
	} catch (err) {
		errorLogger(err as Error, { function: "getWorkedHours", message: "Failed to get worked hours", variables });
		return null;
	}
}

export async function getTimecard(locationId: Nullable<string>, commissionCycleId: Nullable<string>, payPeriodId: Nullable<string>) {
	const query = gql`
		query getTimecard($locationId: String!, $commissionCycleId: String!, $payPeriodId: String!) {
			getTimecard(locationId: $locationId, commissionCycleId: $commissionCycleId, payPeriodId: $payPeriodId) {
				_id
				userId
				fullName
				position
				timecards {
					date
					clockInType
					clockInTime
					clockOutType
					clockOutTime
					regular
					overtime
					totalPaid
					unpaid
				}
				createdAt
				updatedAt
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
	};

	try {
		const response: GraphqlQueryResponse<"getTimecard", IPayPeriodTimesCard[]> = await apiInstance.post("/graphql", {
			query,
			variables,
		});
		return response.data.data?.getTimecard;
	} catch (err) {
		errorLogger(err as Error, { function: "getTimecard", message: "Failed to get timecard", variables });
		return null;
	}
}

/************************************************ PTO ************************************************/
export async function getPto(locationId: Nullable<string>, commissionCycleId: Nullable<string>, payPeriodId: Nullable<string>) {
	const query = gql`
		query getPto($locationId: String!, $commissionCycleId: String!, $payPeriodId: String!) {
			getPto(locationId: $locationId, commissionCycleId: $commissionCycleId, payPeriodId: $payPeriodId) {
				_id
				locationId
				commissionCycleId
				payPeriodId
				usersPto {
					user {
						_id
						fullName
						position
					}
					hours
					payType
					payTypeCode
					payTypeID
					details {
						start
					}
				}
				createdAt
				updatedAt
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
	};

	try {
		const response: GraphqlQueryResponse<"getPto", IPayPeriodPto> = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getPto;
	} catch (err) {
		errorLogger(err as Error, { function: "getPto", message: "Failed to get PTO", variables });
		return null;
	}
}

export async function getPtoBalance(locationId: Nullable<string>, commissionCycleId: Nullable<string>, payPeriodId: Nullable<string>) {
	const query = gql`
		query getPtoBalance($locationId: String!, $commissionCycleId: String!, $payPeriodId: String!) {
			getPtoBalance(locationId: $locationId, commissionCycleId: $commissionCycleId, payPeriodId: $payPeriodId) {
				_id
				locationId
				commissionCycleId
				payPeriodId
				usersPto {
					user {
						_id
						fullName
						position
					}
					hours
					payType
					payTypeCode
					payTypeID
				}
				createdAt
				updatedAt
			}
		}
	`;

	const variables = {
		locationId,
		commissionCycleId,
		payPeriodId,
	};

	try {
		const response = await apiInstance.post("/graphql", { query, variables });
		return response.data.data?.getPtoBalance;
	} catch (err) {
		errorLogger(err as Error, { function: "getPtoBalance", message: "Failed to get PTO balance", variables });
		return null;
	}
}
